import type { Catalog } from '../types';

/**
 * Campos e a tela do buraco.
 *
 * `CR`, `SD`, `SI`, *stroke index*, *fairway*, *par* e *tee* ficam como estão nos dois idiomas —
 * são termos da regra, não copy. Traduzir "tee" para "saída" quebraria a leitura de quem compara
 * com o cartão de papel do clube.
 */
export const course = {
  'courses.searchPlaceholder': {
    pt: 'Buscar por nome ou cidade',
    en: 'Search by name or city',
  },
  'courses.searchAria': { pt: 'Buscar campo', en: 'Search courses' },
  'courses.filtersAria': { pt: 'Filtros', en: 'Filters' },
  'courses.filter.all': { pt: 'Todos', en: 'All' },
  'courses.filter.18': { pt: '18 buracos', en: '18 holes' },
  'courses.filter.9': { pt: '9 buracos', en: '9 holes' },

  'courses.sortedByDistance': {
    pt: 'Ordenado por distância de você',
    en: 'Sorted by distance from you',
  },
  'courses.sortedByName': { pt: 'Ordenado por nome', en: 'Sorted by name' },
  'courses.locating': { pt: 'Localizando…', en: 'Locating…' },
  'courses.useMyLocation': { pt: 'Usar minha localização', en: 'Use my location' },

  'courses.empty.title': { pt: 'Nenhum campo encontrado', en: 'No course found' },
  'courses.empty.clearSearch': { pt: 'Limpar busca', en: 'Clear search' },
  'courses.empty.noMatch': { pt: 'Nada corresponde a “{query}”.', en: 'Nothing matches “{query}”.' },
  'courses.empty.none': {
    pt: 'Ainda não há campos cadastrados no servidor.',
    en: 'There are no courses on the server yet.',
  },

  /** O nome acessível do ✓ ao lado do nome; o `title` completo é o `pill.verifiedTitle`. */
  'courses.verifiedTick': { pt: 'verificado', en: 'verified' },
  'courses.spec': { pt: '{holes} buracos · Par {par}', en: '{holes} holes · Par {par}' },
  'courses.homeTag': { pt: 'meu campo', en: 'my course' },
  'courses.count': {
    pt: { one: '{count} campo', other: '{count} campos' },
    en: { one: '{count} course', other: '{count} courses' },
  },

  // ------------------------------------------------------------------- o campo

  'course.notFound.title': { pt: 'Campo não encontrado', en: 'Course not found' },
  'course.notFound.body': {
    pt: 'Ele pode ter sido removido, ou o link está errado.',
    en: 'It may have been removed, or the link is wrong.',
  },
  'course.notFound.action': { pt: 'Ver todos os campos', en: 'See every course' },

  'course.homeRemoved': { pt: 'Removido dos seus campos', en: 'Removed from your courses' },
  'course.homeSet': { pt: '{name} agora é o seu campo', en: '{name} is now your course' },
  'course.saveFailed': { pt: 'Não foi possível salvar', en: 'Could not save' },

  'course.holes': {
    pt: { one: '{count} buraco', other: '{count} buracos' },
    en: { one: '{count} hole', other: '{count} holes' },
  },
  'course.par': { pt: 'Par {value}', en: 'Par {value}' },
  'course.exploreHoles': { pt: 'Explorar buracos', en: 'Explore the holes' },

  'course.tees': { pt: 'Tees', en: 'Tees' },
  'course.tee': { pt: 'Tee', en: 'Tee' },
  'course.cr': { pt: 'CR', en: 'CR' },
  'course.teeNote': {
    pt: 'Distâncias em {unit} · toque num tee para ver o scorecard por ele',
    en: 'Distances in {unit} · tap a tee to see the scorecard from it',
  },
  'course.scorecard': { pt: 'Scorecard do campo', en: 'Course scorecard' },

  'course.myRounds': { pt: 'Minhas voltas aqui', en: 'My rounds here' },
  'course.noRoundsHere': {
    pt: 'Você ainda não registrou uma volta neste campo.',
    en: 'You have not recorded a round at this course yet.',
  },
  'course.roundMeta': {
    pt: 'Tee {tee} · {holes} buracos',
    en: 'Tee {tee} · {holes} holes',
  },
  'course.isHome': { pt: '✓ Meu campo', en: '✓ My course' },
  'course.setHome': { pt: 'Definir como meu campo', en: 'Set as my course' },

  // ------------------------------------------------------------------ o buraco

  'hole.notFound.title': { pt: 'Buraco não encontrado', en: 'Hole not found' },
  'hole.notFound.body': {
    pt: 'Este campo não tem o buraco {number}.',
    en: 'This course has no hole {number}.',
  },
  'hole.notFound.action': { pt: 'Ver o campo', en: 'See the course' },

  'hole.linkCopied': { pt: 'Link do buraco copiado', en: 'Hole link copied' },
  'hole.copyFailed': { pt: 'Não foi possível copiar o link', en: 'Could not copy the link' },

  'hole.modeAria': { pt: 'Modo de visualização', en: 'View mode' },
  'hole.schematic': { pt: 'Esquemático', en: 'Schematic' },
  'hole.satellite': { pt: 'Satélite', en: 'Satellite' },
  'hole.toSchematic': { pt: 'Mudar para esquemático', en: 'Switch to schematic' },
  'hole.toSatellite': { pt: 'Mudar para satélite', en: 'Switch to satellite' },
  'hole.closeViewer': { pt: 'Fechar visualizador', en: 'Close viewer' },

  'hole.ruler': { pt: 'Régua', en: 'Ruler' },
  'hole.rulerOn': { pt: 'Régua ativa', en: 'Ruler on' },
  'hole.rulerValue': { pt: 'Régua {value} {unit}', en: 'Ruler {value} {unit}' },
  'hole.rulerHint': { pt: 'tee → ponto marcado', en: 'tee → marked point' },
  'hole.rulerPrompt': {
    pt: 'Toque no mapa para medir a partir do tee',
    en: 'Tap the map to measure from the tee',
  },
  'hole.shareHole': { pt: 'Compartilhar buraco', en: 'Share hole' },

  /** Atribuição da imagem: requisito do provedor, e por isso nunca some da tela. */
  'hole.imageryGeneric': { pt: 'Imagens de satélite', en: 'Satellite imagery' },
  /** Só o prefixo muda: os três nomes são exigidos pela licença e não se traduzem. */
  'hole.imageryEsri': {
    pt: 'Imagens: Esri, Maxar, Earthstar Geographics',
    en: 'Imagery: Esri, Maxar, Earthstar Geographics',
  },
  'hole.imagerySchematic': {
    pt: 'Traçado a partir do GeoJSON do campo',
    en: 'Drawn from the course GeoJSON',
  },
  'hole.imageryNoKey': {
    pt: 'VITE_GOOGLE_MAPS_API_KEY não está configurada',
    en: 'VITE_GOOGLE_MAPS_API_KEY is not configured',
  },
  'hole.imageryFallback': {
    pt: 'Google Satellite não pôde ser usado ({reason}); mostrando Esri.',
    en: 'Google Satellite could not be used ({reason}); showing Esri.',
  },

  'hole.teeToPin': { pt: 'Tee → pino', en: 'Tee → pin' },
  'hole.strokeIndex': { pt: 'Stroke index', en: 'Stroke index' },
  'hole.yourScore': { pt: 'Seu resultado', en: 'Your score' },
  'hole.distanceByTee': { pt: 'Distância por tee', en: 'Distance by tee' },
  'hole.rating': { pt: 'CR {cr} · Slope {slope}', en: 'CR {cr} · Slope {slope}' },
  'hole.number': { pt: 'Buraco {number}', en: 'Hole {number}' },
  'hole.teePicker': { pt: 'Tee {name}', en: 'Tee {name}' },

  /** O nome acessível do desenho inteiro — é o que um leitor de tela anuncia no lugar do mapa. */
  'hole.canvasLabel': {
    pt: 'Buraco {number}, par {par}, stroke index {si}, {distance} do tee ao pino',
    en: 'Hole {number}, par {par}, stroke index {si}, {distance} from tee to pin',
  },

  'hole.insight.tooFew': {
    pt: 'Ainda sem histórico suficiente neste buraco.',
    en: 'Not enough history on this hole yet.',
  },
  'hole.insight.sample': {
    pt: '{have} de {need} voltas necessárias',
    en: '{have} of {need} rounds needed',
  },
  /**
   * O trecho em negrito é a **posição do marcador**, e não marcação dentro da mensagem: em
   * inglês a média vem no fim da frase, e é `tx` que monta o nó no lugar certo.
   */
  'hole.insight.average': {
    pt: 'Neste buraco você faz média {average}{clause}',
    en: 'You average {average} on this hole{clause}',
  },
  'hole.insight.detail': {
    pt: {
      one: 'em {count} volta aqui · melhor {best} · pior {worst}',
      other: 'em {count} voltas aqui · melhor {best} · pior {worst}',
    },
    en: {
      one: 'over {count} round here · best {best} · worst {worst}',
      other: 'over {count} rounds here · best {best} · worst {worst}',
    },
  },
  'hole.insight.fairway': { pt: ' · fairway {pct}', en: ' · fairway {pct}' },

  /**
   * O ranking de dificuldade, só até o quinto. Ser o 14º mais difícil de 18 não diz nada, e o
   * `difficultyClause` prefere calar a preencher.
   */
  'hole.rank.1': { pt: 'seu buraco mais difícil', en: 'your hardest hole' },
  'hole.rank.2': { pt: 'seu segundo mais difícil', en: 'your second hardest' },
  'hole.rank.3': { pt: 'seu terceiro mais difícil', en: 'your third hardest' },
  'hole.rank.4': { pt: 'seu quarto mais difícil', en: 'your fourth hardest' },
  'hole.rank.5': { pt: 'seu quinto mais difícil', en: 'your fifth hardest' },

  'hole.yourResultHere': { pt: 'Seu resultado aqui', en: 'Your score here' },
  'hole.strokes': {
    pt: { one: '{count} tacada', other: '{count} tacadas' },
    en: { one: '{count} stroke', other: '{count} strokes' },
  },
  'hole.putts': {
    pt: { one: '{count} putt', other: '{count} putts' },
    en: { one: '{count} putt', other: '{count} putts' },
  },
  'hole.fairwayHit': { pt: 'fairway acertado', en: 'fairway hit' },
  'hole.fairwayMissed': { pt: 'fairway errado', en: 'fairway missed' },
} as const satisfies Catalog;
